import React from 'react';
import { MessageSquare } from 'lucide-react';
import { useGlobalLoaderStore } from '@/store/globalLoaderStore';
import { cn } from '@/lib/utils';

export interface GlobalLoaderProps {
  variant?: 'fullscreen' | 'container' | 'card' | 'inline';
  title?: string;
  message?: string;
  submessage?: string;
  showTimeoutWarning?: boolean;
  onRetry?: () => void;
  onLogout?: () => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
}

export function GlobalLoader({
  variant = 'fullscreen',
  title,
  message,
  submessage,
  showTimeoutWarning,
  onRetry,
  onLogout,
  className,
  size = 'md',
  icon
}: GlobalLoaderProps) {
  const store = useGlobalLoaderStore();

  const effectiveTitle = title ?? (variant === 'fullscreen' ? (store.message || 'Task CRM') : undefined);
  const effectiveMessage = message ?? submessage ?? (variant === 'fullscreen' ? store.submessage : 'Carregando dados...');
  const effectiveShowWarning = showTimeoutWarning ?? (variant === 'fullscreen' ? store.showTimeoutWarning : false);

  // Tamanhos do anel e do ícone central
  const dimensions = {
    sm: {
      orbit: 'w-12 h-12',
      box: 'w-7 h-7',
      iconSize: 14,
      border: 'border-2',
      fontSizeTitle: 'text-sm font-bold',
      fontSizeMessage: 'text-xs'
    },
    md: {
      orbit: 'w-20 h-20',
      box: 'w-12 h-12',
      iconSize: 20,
      border: 'border-4',
      fontSizeTitle: 'text-lg font-bold',
      fontSizeMessage: 'text-sm'
    },
    lg: {
      orbit: 'w-24 h-24',
      box: 'w-14 h-14',
      iconSize: 24,
      border: 'border-4',
      fontSizeTitle: 'text-xl font-extrabold',
      fontSizeMessage: 'text-base'
    }
  }[size];

  const renderIcon = () => {
    if (icon) return icon;
    return (
      <MessageSquare
        size={dimensions.iconSize}
        className="text-blue-600 dark:text-blue-400 animate-pulse"
      />
    );
  };

  const loaderCore = (
    <div className="relative flex flex-col items-center max-w-xs text-center z-10 select-none">
      {/* Anel de órbita animado idêntico ao index.html */}
      <div className={cn("relative flex items-center justify-center mb-4 sm:mb-6", dimensions.orbit)}>
        <div className={cn("absolute inset-0 rounded-full border-blue-500/15 dark:border-blue-400/10", dimensions.border)} />
        <div
          className={cn(
            "absolute inset-0 rounded-full border-transparent border-t-blue-600 dark:border-t-blue-400 animate-spin",
            dimensions.border
          )}
          style={{ animationDuration: '0.8s' }}
        />

        {/* Caixa central do emblema */}
        <div className={cn(
          "rounded-full flex items-center justify-center shadow-md dark:shadow-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-[#151D24]",
          dimensions.box
        )}>
          {renderIcon()}
        </div>
      </div>

      {effectiveTitle && (
        <h3 className={cn("text-gray-900 dark:text-white tracking-tight mb-1", dimensions.fontSizeTitle)}>
          {effectiveTitle}
        </h3>
      )}

      {effectiveMessage && (
        <p className={cn("font-medium text-gray-500 dark:text-gray-400 animate-pulse", dimensions.fontSizeMessage)}>
          {effectiveMessage}
        </p>
      )}

      {effectiveShowWarning && (
        <div className="mt-6 p-4 bg-white dark:bg-[#111B21] border border-gray-200 dark:border-gray-800 rounded-2xl shadow-lg space-y-4 max-w-sm animate-in fade-in slide-in-from-bottom-2 duration-300">
          <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
            A inicialização está demorando mais que o esperado devido a oscilações de rede.
          </p>
          <div className="flex gap-2 justify-center">
            {onRetry && (
              <button
                onClick={onRetry}
                className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl shadow-xs transition-colors cursor-pointer"
              >
                Tentar Novamente
              </button>
            )}
            {onLogout && (
              <button
                onClick={onLogout}
                className="px-3.5 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-semibold rounded-xl transition-colors cursor-pointer"
              >
                Sair
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  if (variant === 'inline') {
    return (
      <div className={cn("inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 font-medium", className)}>
        <div className="w-4 h-4 border-2 border-blue-500/20 border-t-blue-600 dark:border-t-blue-400 rounded-full animate-spin shrink-0" style={{ animationDuration: '0.8s' }} />
        {effectiveMessage && <span className="animate-pulse">{effectiveMessage}</span>}
      </div>
    );
  }

  if (variant === 'card' || variant === 'container') {
    return (
      <div className={cn(
        "bg-white dark:bg-[#111B21] rounded-2xl border border-gray-200 dark:border-gray-800 p-8 sm:p-12 flex flex-col items-center justify-center text-center relative overflow-hidden transition-colors duration-300",
        className
      )}>
        {/* Glow sutil */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-blue-500/5 rounded-full blur-2xl pointer-events-none" />
        {loaderCore}
      </div>
    );
  }

  // Fullscreen (padrão)
  return (
    <div className={cn(
      "min-h-screen bg-[#F8FAFC] dark:bg-[#090D10] text-[#0F172A] dark:text-[#FFFFFF] flex flex-col items-center justify-center p-6 relative overflow-hidden transition-colors duration-300 font-sans",
      className
    )}>
      {/* Glow decorativo de fundo */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-blue-500/10 dark:bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />
      {loaderCore}
    </div>
  );
}

export default GlobalLoader;
